import { useState, useCallback } from "react";
import {
  ApprovalEntity,
  PaginationInfoEntity,
} from "../../domain/entities/ApprovalEntity";
import { getMyRequests } from "../../data/api/approvalsApi";
import { ApprovalMapper } from "../../data/mappers/ApprovalMapper";

export interface UseMyRequestsParams {
  entityType?: string;
  limit?: number;
  offset?: number;
  search?: string;
}

export interface UseMyRequestsReturn {
  myRequests: ApprovalEntity[];
  pagination: PaginationInfoEntity | null;
  loading: boolean;
  error: string | null;
  fetchMyRequests: (params?: UseMyRequestsParams) => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
}

export const useMyRequests = (
  initialParams?: UseMyRequestsParams,
): UseMyRequestsReturn => {
  const [myRequests, setMyRequests] = useState<ApprovalEntity[]>([]);
  const [pagination, setPagination] = useState<PaginationInfoEntity | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentParams, setCurrentParams] = useState<
    UseMyRequestsParams | undefined
  >(initialParams);

  /**
   * Fetch my review requests with filters and pagination
   */
  const fetchMyRequests = useCallback(
    async (params?: UseMyRequestsParams): Promise<void> => {
      setLoading(true);
      setError(null);

      const paramsToUse = params || currentParams || {};

      // Clear my requests immediately if entity type changed to show loader
      const entityTypeChanged =
        paramsToUse.entityType !== currentParams?.entityType;

      if (entityTypeChanged) {
        setMyRequests([]);
        setPagination(null);
      }

      try {
        const response = await getMyRequests(paramsToUse);
        const result = ApprovalMapper.toApprovalsResultEntity(response);
        setMyRequests(result.approvals);
        setPagination(result.pagination);
        setCurrentParams(paramsToUse);
      } catch (err: any) {
        const errorMessage = err.message || "Failed to load my requests";
        setError(errorMessage);
        console.error("Error fetching my requests:", err);
      } finally {
        setLoading(false);
      }
    },
    [currentParams],
  );

  /**
   * Refresh my requests with current params
   */
  const refresh = useCallback(async (): Promise<void> => {
    await fetchMyRequests(currentParams);
  }, [fetchMyRequests, currentParams]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    myRequests,
    pagination,
    loading,
    error,
    fetchMyRequests,
    refresh,
    clearError,
  };
};
